import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Icons } from '@/components/ui/icons';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { BirthdayPicker } from '@/components/ui/birthday-picker';
import { Checkbox } from '@/components/ui/check-box';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/template/lib/toast';
import { api } from '@/lib/api';
import { DocumentUpload } from '@/components/features/verification/document-upload';
import { KV_REGIONS, SERVICE_TYPES, type ServiceTypeValue } from '@/lib/constants/german_regions';
import { SPECIALIZATIONS_SELECT_ORDER } from '../../../convex/lib/medicalSpecializations';
import { validateE164PhoneNumber, normalizePhoneNumber } from '../../../convex/lib/phoneValidation';
import { type OnboardingMe, type OnboardingProfile, type OnboardingServicePreferences } from '@/types/users';
import { type WizardStep, SUBSTITUTE_STEPS, PROVIDER_STEPS } from '@/types/modals';
import { type BaseModalProps } from '@/types/modal-props';
import { trackOnboardingCompleted } from '@/lib/analytics';

interface Props extends BaseModalProps {
  me: OnboardingMe | null;
  profile: OnboardingProfile | null;
  servicePreferences: OnboardingServicePreferences | null;
}

export const OnboardingDialog: React.FC<Props> = ({ open, onOpenChange, me, profile, servicePreferences }) => {
  const { t } = useTranslation('onboarding');
  const completeOnboarding = useMutation(api.app.users.mutations.completeOnboarding as any);
  const updateProfile = useMutation(api.app.users.mutations.upsertMyUserProfile as any);
  const saveProviderPersonalInfo = useMutation(api.app.users.mutations.saveProviderPersonalInfo as any);
  const saveServiceAreaPreferences = useMutation(api.app.profileMutations.saveServiceAreaPreferences as any);
  const upsertBillingInfo = useMutation(api.app.billing.userQueries.upsertBillingInfo as any);
  const verificationStatus = useQuery(api.app.verification.getMyVerificationStatus as any, {});

  // DA-343: Use appropriate steps based on role
  const STEPS = me?.role === 'provider' ? PROVIDER_STEPS : SUBSTITUTE_STEPS;

  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [birthday, setBirthday] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressPostalCode, setAddressPostalCode] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [phone, setPhone] = useState(''); // Phase 5: Mandatory phone number

  // DA-387: Provider-specific state variables
  const [altPhone, setAltPhone] = useState('');
  const [landline, setLandline] = useState('');
  const [fax, setFax] = useState('');
  const [isMvz, setIsMvz] = useState(false);
  const [invoiceName, setInvoiceName] = useState('');
  const [bsnr, setBsnr] = useState('');
  const [doctors, setDoctors] = useState<Array<{ fullName: string; lanr: string; kvSitz: string }>>([{ fullName: '', lanr: '', kvSitz: '' }]);

  // DA-263: Kept for backward compatibility with existing user preferences, but not used in UI
  const [_searchMode, setSearchMode] = useState<'federal' | 'kv' | 'distance'>('kv');
  const [_selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedKvs, setSelectedKvs] = useState<string[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeValue[]>([]);
  const [highCompOverride, setHighCompOverride] = useState(false);
  const [minComp, setMinComp] = useState(100);

  const [thirdYearConfirmed, setThirdYearConfirmed] = useState(false);

  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const [showDocs, setShowDocs] = useState(false);
  const [insuranceCompany, setInsuranceCompany] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');

  const labels: Record<WizardStep, string> = {
    intro: t('dialog.steps.intro'),
    personal: t('dialog.steps.personal'),
    qualifications: t('dialog.steps.qualifications'),
    address: t('dialog.steps.address'),
    recommendations: t('dialog.steps.recommendations'),
    documents: t('dialog.steps.documents'),
    location: t('dialog.steps.location'),
  };

  const markTouched = (field: string) => {
    setTouchedFields((prev) => new Set(prev).add(field));
  };

  const shouldShowError = (field: string, isValid: boolean) => {
    return touchedFields.has(field) && !isValid;
  };

  const prevOpenRef = React.useRef(open);

  useEffect(() => {
    // Only reset form when dialog opens (transitions from closed to open)
    const isOpening = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (!isOpening) return;

    setStepIndex(0);
    setError(null);
    setTouchedFields(new Set());

    setFirstName(me?.firstName || '');
    setLastName(me?.lastName || '');
    setAddressStreet(me?.address?.street || '');
    setAddressPostalCode(me?.address?.postalCode || '');
    setAddressCity(me?.address?.city || '');
    setPhone(profile?.phone || '');

    // DA-387: Reset provider-specific fields
    setAltPhone(profile?.altPhone || '');
    setLandline(profile?.landline || '');
    setFax(profile?.fax || '');
    setIsMvz(profile?.isMvz || false);
    // Default invoice name to firstName + lastName
    const defaultInvoiceName = [me?.firstName, me?.lastName].filter(Boolean).join(' ');
    setInvoiceName(defaultInvoiceName || '');
    setBsnr('');
    setDoctors([{ fullName: '', lanr: '', kvSitz: '' }]);

    setTitle(profile?.title || '');
    if (profile?.birthday) {
      const d = new Date(profile.birthday);
      const yyyy = d.getUTCFullYear();
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(d.getUTCDate()).padStart(2, '0');
      setBirthday(`${yyyy}-${mm}-${dd}`);
    } else {
      setBirthday('');
    }
    // Initialize specialization (use first item if array has values)
    setSpecialization(profile?.specialization?.[0] || '');

    const prefs = servicePreferences ?? null;
    if (prefs) {
      setSearchMode(prefs.searchMode);
      setSelectedStates(prefs.selectedStates || []);
      setSelectedKvs(prefs.selectedKvRegions || []);
      setServiceTypes((prefs.preferredServiceTypes as ServiceTypeValue[]) || []);
      setHighCompOverride(prefs.enableHighCompOverride);
      setMinComp(prefs.minCompensationOverride ?? 100);
    } else {
      setSearchMode('kv');
      setSelectedStates([]);
      setSelectedKvs([]);
      setServiceTypes([]);
      setHighCompOverride(false);
      setMinComp(100);
    }

    setThirdYearConfirmed(false);

    setShowDocs(false);
    setInsuranceCompany('');
    setPolicyNumber('');
    setValidFrom('');
    setValidTo('');
  }, [open, me, profile, servicePreferences]);

  // DA-343: Support both substitute and provider onboarding
  if (!me || (me.role !== 'substitute' && me.role !== 'provider')) return null;

  const nextStep = () => setStepIndex((prev) => Math.min(prev + 1, STEPS.length - 1));
  const prevStep = () => setStepIndex((prev) => Math.max(prev - 1, 0));

  // DA-157, DA-163 & DA-166: E.164 phone validation (matches server validation)
  const phoneValidation = validateE164PhoneNumber(phone);
  const isPhoneValid = phoneValidation.valid;

  // DA-343: Different validation for providers vs substitutes
  const isProvider = me?.role === 'provider';

  // DA-387: Provider validation split into personal and location steps
  const providerMvzDoctorsValid = !isMvz || doctors.every((d) => d.fullName.trim() && d.lanr.trim());
  const providerPersonalInvalid = !firstName.trim() || !lastName.trim() || !isPhoneValid;
  const providerLocationInvalid = !invoiceName.trim() || !addressStreet.trim() || !addressPostalCode.trim() || !addressCity.trim() || (isMvz && !providerMvzDoctorsValid);

  // DA-435: Split substitute validation into 3 step-specific validators
  // Step 2: Personal - basic info only (5 fields: firstName, lastName, title, birthday, phone)
  const substitutePersonalInvalid = !firstName.trim() || !lastName.trim() || !birthday || !isPhoneValid;

  // Step 3: Qualifications - specialization and third-year confirmation
  const substituteQualificationsInvalid = !specialization || !thirdYearConfirmed;

  // Step 4: Address - street, postal code, city
  const substituteAddressInvalid = !addressStreet.trim() || !addressPostalCode.trim() || !addressCity.trim();

  const personalInvalid = isProvider ? providerPersonalInvalid : substitutePersonalInvalid;
  const locationInvalid = isProvider ? providerLocationInvalid : false;
  const recommendationsInvalid = serviceTypes.length === 0 || selectedKvs.length === 0;

  // DA-435: Updated canAdvance to include new steps
  const canAdvance =
    step === 'personal'
      ? !personalInvalid
      : step === 'qualifications'
        ? !substituteQualificationsInvalid
        : step === 'address'
          ? !substituteAddressInvalid
          : step === 'location' && isProvider
            ? !locationInvalid
            : step === 'recommendations'
              ? !recommendationsInvalid
              : true;

  const savePersonal = async () => {
    // DA-435: Mark different fields as touched based on role
    if (isProvider) {
      setTouchedFields(new Set(['firstName', 'lastName', 'phone']));
    } else {
      // DA-435: Substitute personal step only validates these fields now
      setTouchedFields(new Set(['firstName', 'lastName', 'birthday', 'phone']));
    }

    if (personalInvalid) {
      setError(t('errors.fillAllFields'));
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      if (isProvider) {
        // DA-387: Save provider personal info to database (so it's not lost on refresh)
        await saveProviderPersonalInfo({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          title: title.trim() || undefined,
          phone: normalizePhoneNumber(phone),
          altPhone: altPhone.trim() || undefined,
          landline: landline.trim() || undefined,
          fax: fax.trim() || undefined,
          isMvz: isMvz || undefined,
        });

        // Update invoice name default if not set
        if (!invoiceName.trim()) {
          setInvoiceName([firstName.trim(), lastName.trim()].filter(Boolean).join(' '));
        }
        toast.success(t('toast.personalSaved'));
        nextStep();
      } else {
        // DA-435: Substitute personal step - only save title, birthday, phone
        // Specialization, address, and thirdYear are saved in subsequent steps
        const profileUpdate: {
          title?: string;
          birthday?: number;
          phone?: string;
        } = {};
        if (title) profileUpdate.title = title;
        if (birthday) profileUpdate.birthday = new Date(birthday).getTime();
        if (phone) profileUpdate.phone = normalizePhoneNumber(phone);
        await updateProfile(profileUpdate);

        toast.success(t('toast.personalSaved'));
        nextStep();
      }
    } catch (err: any) {
      setError(err?.message || t('errors.personalSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // DA-435: Save substitute qualifications (step 3 of 6)
  const saveQualifications = async () => {
    setTouchedFields(new Set(['specialization', 'thirdYearConfirmed']));

    if (substituteQualificationsInvalid) {
      setError(t('errors.fillAllFields'));
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const profileUpdate: {
        specialization?: string[];
        thirdYearConfirmed?: boolean;
        thirdYearConfirmedDate?: number;
      } = {};
      if (specialization) profileUpdate.specialization = [specialization];
      if (thirdYearConfirmed) {
        profileUpdate.thirdYearConfirmed = true;
        profileUpdate.thirdYearConfirmedDate = Date.now();
      }
      await updateProfile(profileUpdate);

      toast.success(t('toast.qualificationsSaved'));
      nextStep();
    } catch (err: any) {
      setError(err?.message || t('errors.qualificationsSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // DA-435: Save substitute address and complete onboarding (step 4 of 6)
  const saveAddress = async () => {
    setTouchedFields(new Set(['addressStreet', 'addressPostalCode', 'addressCity']));

    if (substituteAddressInvalid) {
      setError(t('errors.fillAllFields'));
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      // Complete onboarding with address data - this marks the user as onboarded
      await completeOnboarding({
        role: 'substitute',
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        address: {
          street: addressStreet.trim(),
          postalCode: addressPostalCode.trim(),
          city: addressCity.trim(),
        },
      });

      // DA-435: Also save address to billing info for invoice generation
      // The invoice address card reads from billingInfos.address, not userProfiles.location
      await upsertBillingInfo({
        invoiceName: `${firstName.trim()} ${lastName.trim()}`,
        address: {
          street: addressStreet.trim(),
          postalCode: addressPostalCode.trim(),
          city: addressCity.trim(),
          country: 'Deutschland',
        },
      });

      toast.success(t('toast.addressSaved'));
      nextStep();
    } catch (err: any) {
      setError(err?.message || t('errors.addressSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // DA-387: Save provider location and complete onboarding
  const saveProviderLocation = async () => {
    const locationFields = ['invoiceName', 'addressStreet', 'addressPostalCode', 'addressCity'];
    if (isMvz) {
      doctors.forEach((_, idx) => {
        locationFields.push(`doctor-${idx}-fullName`, `doctor-${idx}-lanr`);
      });
    }
    setTouchedFields(new Set(locationFields));

    if (locationInvalid) {
      setError(t('errors.fillAllFields'));
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const locationInfo: {
        invoiceName: string;
        street: string;
        postalCode: string;
        city: string;
        bsnr?: string;
        doctors?: Array<{ fullName: string; lanr: string; kvSitz?: string }>;
      } = {
        invoiceName: invoiceName.trim(),
        street: addressStreet.trim(),
        postalCode: addressPostalCode.trim(),
        city: addressCity.trim(),
      };

      // Add BSNR if provided
      if (bsnr.trim()) {
        locationInfo.bsnr = bsnr.trim();
      }

      // Add doctors array if MVZ
      if (isMvz && doctors.length > 0) {
        locationInfo.doctors = doctors.map((d) => ({
          fullName: d.fullName.trim(),
          lanr: d.lanr.trim(),
          ...(d.kvSitz && { kvSitz: d.kvSitz }),
        }));
      }

      await completeOnboarding({
        role: 'provider',
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        title: title.trim() || undefined,
        phone: normalizePhoneNumber(phone),
        altPhone: altPhone.trim() || undefined,
        landline: landline.trim() || undefined,
        fax: fax.trim() || undefined,
        isMvz: isMvz || undefined,
        locationInfo,
      });

      trackOnboardingCompleted('provider');
      toast.success(t('toast.onboardingComplete'));
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message || t('errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const savePreferences = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const prefsUpdate: {
        searchMode: 'federal' | 'kv' | 'distance';
        selectedStates?: string[];
        selectedKvRegions?: string[];
        cityCenter?: { city: string; coordinates: number[] };
        distanceRadiusKm?: number;
        preferredServiceTypes: ServiceTypeValue[];
        enableHighCompOverride: boolean;
        minCompensationOverride?: number;
      } = {
        searchMode: 'kv',
        selectedKvRegions: selectedKvs,
        preferredServiceTypes: serviceTypes,
        enableHighCompOverride: highCompOverride,
      };
      if (highCompOverride) prefsUpdate.minCompensationOverride = minComp;
      await saveServiceAreaPreferences(prefsUpdate);
      toast.success(t('toast.preferencesSaved'));
      nextStep();
    } catch (err: any) {
      setError(err?.message || t('errors.preferencesSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinish = () => {
    trackOnboardingCompleted(me?.role === 'provider' || me?.role === 'substitute' ? me.role : 'unknown');
    toast.success(t('toast.onboardingComplete'));
    onOpenChange(false);
  };

  const renderIntro = () => {
    // DA-387: Different intro content for providers vs substitutes
    if (isProvider) {
      return (
        <Card className='border-border bg-card'>
          <CardContent className='space-y-4 pt-6'>
            <div className='space-y-2'>
              <h2 className='text-lg font-semibold'>{t('intro.provider.title')}</h2>
              <p className='text-sm text-muted-foreground'>{t('intro.provider.description')}</p>
            </div>
            <div className='space-y-3 rounded-lg border p-4'>
              <div className='flex items-start gap-3'>
                <Icons.IdCard className='h-5 w-5 text-primary' />
                <div>
                  <p className='text-sm font-medium'>{t('intro.provider.personalInfo.title')}</p>
                  <p className='text-xs text-muted-foreground'>{t('intro.provider.personalInfo.description')}</p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <Icons.MapPin className='h-5 w-5 text-primary' />
                <div>
                  <p className='text-sm font-medium'>{t('intro.provider.location.title')}</p>
                  <p className='text-xs text-muted-foreground'>{t('intro.provider.location.description')}</p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <Icons.Building className='h-5 w-5 text-primary' />
                <div>
                  <p className='text-sm font-medium'>{t('intro.provider.mvz.title')}</p>
                  <p className='text-xs text-muted-foreground'>{t('intro.provider.mvz.description')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // DA-435: Updated substitute intro to reflect new 6-step flow
    return (
      <Card className='border-border bg-card'>
        <CardContent className='space-y-4 pt-6'>
          <div className='space-y-2'>
            <h2 className='text-lg font-semibold'>{t('intro.substitute.title')}</h2>
            <p className='text-sm text-muted-foreground'>{t('intro.substitute.description')}</p>
          </div>
          <div className='space-y-3 rounded-lg border p-4'>
            <div className='flex items-start gap-3'>
              <Icons.IdCard className='h-5 w-5 text-primary' />
              <div>
                <p className='text-sm font-medium'>{t('intro.substitute.personalInfo.title')}</p>
                <p className='text-xs text-muted-foreground'>{t('intro.substitute.personalInfo.description')}</p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <Icons.Stethoscope className='h-5 w-5 text-primary' />
              <div>
                <p className='text-sm font-medium'>{t('intro.substitute.qualifications.title')}</p>
                <p className='text-xs text-muted-foreground'>{t('intro.substitute.qualifications.description')}</p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <Icons.MapPin className='h-5 w-5 text-primary' />
              <div>
                <p className='text-sm font-medium'>{t('intro.substitute.address.title')}</p>
                <p className='text-xs text-muted-foreground'>{t('intro.substitute.address.description')}</p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <Icons.Settings className='h-5 w-5 text-primary' />
              <div>
                <p className='text-sm font-medium'>{t('intro.substitute.preferences.title')}</p>
                <p className='text-xs text-muted-foreground'>{t('intro.substitute.preferences.description')}</p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <Icons.ShieldCheck className='h-5 w-5 text-primary' />
              <div>
                <p className='text-sm font-medium'>{t('intro.substitute.documents.title')}</p>
                <p className='text-xs text-muted-foreground'>{t('intro.substitute.documents.description')}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // DA-387: Helper functions for doctors array management
  const handleAddDoctor = () => {
    setDoctors([...doctors, { fullName: '', lanr: '', kvSitz: '' }]);
  };

  const handleRemoveDoctor = (index: number) => {
    if (doctors.length > 1) {
      setDoctors(doctors.filter((_, i) => i !== index));
    }
  };

  const updateDoctor = (index: number, field: 'fullName' | 'lanr' | 'kvSitz', value: string) => {
    const updated = [...doctors];
    updated[index] = { ...updated[index], [field]: value };
    setDoctors(updated);
  };

  // DA-387: Update invoice name when firstName or lastName changes
  const handleFirstNameChange = (value: string) => {
    setFirstName(value);
    // Auto-update invoice name if it hasn't been manually edited
    if (isProvider && invoiceName === [firstName, lastName].filter(Boolean).join(' ')) {
      setInvoiceName([value, lastName].filter(Boolean).join(' '));
    }
  };

  const handleLastNameChange = (value: string) => {
    setLastName(value);
    // Auto-update invoice name if it hasn't been manually edited
    if (isProvider && invoiceName === [firstName, lastName].filter(Boolean).join(' ')) {
      setInvoiceName([firstName, value].filter(Boolean).join(' '));
    }
  };

  const renderPersonal = () => {
    // DA-387: Provider personal info form (step 2 of 3)
    if (isProvider) {
      return (
        <div className='space-y-6'>
          {/* Personal Info Section */}
          <div className='space-y-4'>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <Label htmlFor='firstName'>
                  {t('personal.firstName')}
                  <span className='text-destructive ml-1'>*</span>
                </Label>
                <Input
                  id='firstName'
                  value={firstName}
                  onChange={(event) => handleFirstNameChange(event.target.value)}
                  onBlur={() => markTouched('firstName')}
                  autoComplete='given-name'
                  aria-invalid={shouldShowError('firstName', firstName.trim().length > 0)}
                  aria-describedby={shouldShowError('firstName', firstName.trim().length > 0) ? 'firstName-error' : undefined}
                />
                {shouldShowError('firstName', firstName.trim().length > 0) && (
                  <p id='firstName-error' className='text-xs text-destructive' role='alert'>
                    {t('validation.firstNameRequired')}
                  </p>
                )}
              </div>
              <div className='space-y-2'>
                <Label htmlFor='lastName'>
                  {t('personal.lastName')}
                  <span className='text-destructive ml-1'>*</span>
                </Label>
                <Input
                  id='lastName'
                  value={lastName}
                  onChange={(event) => handleLastNameChange(event.target.value)}
                  onBlur={() => markTouched('lastName')}
                  autoComplete='family-name'
                  aria-invalid={shouldShowError('lastName', lastName.trim().length > 0)}
                  aria-describedby={shouldShowError('lastName', lastName.trim().length > 0) ? 'lastName-error' : undefined}
                />
                {shouldShowError('lastName', lastName.trim().length > 0) && (
                  <p id='lastName-error' className='text-xs text-destructive' role='alert'>
                    {t('validation.lastNameRequired')}
                  </p>
                )}
              </div>
              <div className='space-y-2'>
                <Label htmlFor='title'>{t('personal.title')}</Label>
                <Input id='title' value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t('personal.titlePlaceholder')} />
              </div>
            </div>

            {/* Phone fields - all visible */}
            <Separator />
            <p className='text-sm font-medium'>{t('personal.contactData')}</p>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <Label htmlFor='phone'>
                  {t('personal.phoneMobile')}
                  <span className='text-destructive ml-1'>*</span>
                </Label>
                <Input
                  id='phone'
                  type='tel'
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  onBlur={() => markTouched('phone')}
                  placeholder='+49 170 1234567'
                  autoComplete='tel'
                  aria-invalid={shouldShowError('phone', isPhoneValid)}
                  aria-describedby={shouldShowError('phone', isPhoneValid) ? 'phone-error' : 'phone-description'}
                />
                {shouldShowError('phone', isPhoneValid) && (
                  <p id='phone-error' className='text-xs text-destructive' role='alert'>
                    {!phone.trim() ? t('validation.phoneRequired') : phoneValidation.error}
                  </p>
                )}
                {!shouldShowError('phone', isPhoneValid) && (
                  <p id='phone-description' className='text-xs text-muted-foreground'>
                    {t('personal.phoneFormat')}
                  </p>
                )}
              </div>
              <div className='space-y-2'>
                <Label htmlFor='altPhone'>{t('personal.phoneAlt')}</Label>
                <Input id='altPhone' type='tel' value={altPhone} onChange={(event) => setAltPhone(event.target.value)} placeholder='+49 171 9876543' autoComplete='tel' />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='landline'>{t('personal.landline')}</Label>
                <Input id='landline' type='tel' value={landline} onChange={(event) => setLandline(event.target.value)} placeholder='+49 30 12345678' />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='fax'>{t('personal.fax')}</Label>
                <Input id='fax' type='tel' value={fax} onChange={(event) => setFax(event.target.value)} placeholder='+49 30 12345679' />
              </div>
            </div>

            {/* MVZ Checkbox */}
            <div className='flex items-start gap-3 rounded-md border border-border/80 p-3 bg-muted/20'>
              <Checkbox id='isMvz' checked={isMvz} onCheckedChange={(checked: boolean) => setIsMvz(checked === true)} className='mt-0.5' />
              <div className='space-y-1'>
                <Label htmlFor='isMvz' className='cursor-pointer font-normal'>
                  {t('personal.mvzCheckbox')}
                </Label>
                <p className='text-xs text-muted-foreground'>{t('personal.mvzDescription')}</p>
              </div>
            </div>
          </div>

          <Alert>
            <Icons.Info className='h-4 w-4' />
            <AlertDescription className='text-xs'>{t('personal.dataUsageProvider')}</AlertDescription>
          </Alert>
        </div>
      );
    }

    // DA-435: Simplified substitute form - only personal info and contact (step 2 of 6)
    // Specialization, address, and third-year moved to separate steps
    return (
      <div className='space-y-6'>
        {/* Name Section */}
        <div className='space-y-4'>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='firstName'>
                {t('personal.firstName')}
                <span className='text-destructive ml-1'>*</span>
              </Label>
              <Input
                id='firstName'
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                onBlur={() => markTouched('firstName')}
                autoComplete='given-name'
                aria-invalid={shouldShowError('firstName', firstName.trim().length > 0)}
                aria-describedby={shouldShowError('firstName', firstName.trim().length > 0) ? 'firstName-error' : undefined}
              />
              {shouldShowError('firstName', firstName.trim().length > 0) && (
                <p id='firstName-error' className='text-xs text-destructive' role='alert'>
                  {t('validation.firstNameRequired')}
                </p>
              )}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='lastName'>
                {t('personal.lastName')}
                <span className='text-destructive ml-1'>*</span>
              </Label>
              <Input
                id='lastName'
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                onBlur={() => markTouched('lastName')}
                autoComplete='family-name'
                aria-invalid={shouldShowError('lastName', lastName.trim().length > 0)}
                aria-describedby={shouldShowError('lastName', lastName.trim().length > 0) ? 'lastName-error' : undefined}
              />
              {shouldShowError('lastName', lastName.trim().length > 0) && (
                <p id='lastName-error' className='text-xs text-destructive' role='alert'>
                  {t('validation.lastNameRequired')}
                </p>
              )}
            </div>
          </div>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='title'>{t('personal.title')}</Label>
              <Input id='title' value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t('personal.titlePlaceholder')} />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='birthday'>
                {t('personal.birthday')}
                <span className='text-destructive ml-1'>*</span>
              </Label>
              <BirthdayPicker
                id='birthday'
                value={birthday}
                onChange={(value) => {
                  setBirthday(value);
                  markTouched('birthday');
                }}
                requireMinAge={18}
                futureYears={0}
                aria-invalid={shouldShowError('birthday', !!birthday)}
                aria-describedby={shouldShowError('birthday', !!birthday) ? 'birthday-error' : undefined}
              />
              {shouldShowError('birthday', !!birthday) && (
                <p id='birthday-error' className='text-xs text-destructive' role='alert'>
                  {t('validation.birthdayRequired')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <Separator />
        <p className='text-sm font-medium'>{t('personal.contactData')}</p>
        <div className='space-y-2'>
          <Label htmlFor='phone'>
            {t('personal.phone')}
            <span className='text-destructive ml-1'>*</span>
          </Label>
          <Input
            id='phone'
            type='tel'
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            onBlur={() => markTouched('phone')}
            placeholder='+49 123 456 7890'
            autoComplete='tel'
            aria-invalid={shouldShowError('phone', isPhoneValid)}
            aria-describedby={shouldShowError('phone', isPhoneValid) ? 'phone-error' : 'phone-description'}
          />
          {(() => {
            const showError = shouldShowError('phone', isPhoneValid);

            if (!showError) {
              return (
                <p id='phone-description' className='text-xs text-muted-foreground'>
                  {t('personal.phoneRequired')}
                </p>
              );
            }

            if (!phone.trim()) {
              return (
                <p id='phone-error' className='text-xs text-destructive' role='alert'>
                  {t('validation.phoneRequired')}
                </p>
              );
            }

            if (!phoneValidation.valid) {
              return (
                <p id='phone-error' className='text-xs text-destructive' role='alert'>
                  {phoneValidation.error}
                </p>
              );
            }

            return (
              <p id='phone-description' className='text-xs text-muted-foreground'>
                {t('personal.phoneRequired')}
              </p>
            );
          })()}
        </div>

        <Alert>
          <Icons.Info className='h-4 w-4' />
          <AlertDescription className='text-xs'>{t('personal.dataUsageSubstitute')}</AlertDescription>
        </Alert>
      </div>
    );
  };

  // DA-435: New step for substitute qualifications (step 3 of 6)
  const renderQualifications = () => (
    <div className='space-y-6'>
      {/* Specialization Section */}
      <div className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='specialization'>
            {t('personal.specialization')}
            <span className='text-destructive ml-1'>*</span>
          </Label>
          <Select
            value={specialization}
            onValueChange={(value: string | null) => {
              if (value === null) return;
              setSpecialization(value);
              markTouched('specialization');
            }}
          >
            <SelectTrigger
              id='specialization'
              onBlur={() => markTouched('specialization')}
              aria-invalid={shouldShowError('specialization', !!specialization)}
              aria-describedby={shouldShowError('specialization', !!specialization) ? 'specialization-error' : 'specialization-description'}
            >
              <SelectValue placeholder={t('personal.specializationPlaceholder')} />
            </SelectTrigger>
            <SelectContent className='max-h-[300px]'>
              {SPECIALIZATIONS_SELECT_ORDER.map((spec) => (
                <SelectItem key={spec} value={spec}>
                  {spec}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {shouldShowError('specialization', !!specialization) && (
            <p id='specialization-error' className='text-xs text-destructive' role='alert'>
              {t('validation.specializationRequired')}
            </p>
          )}
          <p id='specialization-description' className='text-xs text-muted-foreground'>
            {t('personal.specializationDescription')}
          </p>
        </div>
      </div>

      {/* Third Year Confirmation */}
      <div className='space-y-4 rounded-lg border border-dashed border-border/80 p-4 bg-muted/20'>
        <p className='text-sm font-medium'>{t('personal.thirdYear.title')}</p>
        <div className='flex items-start gap-3'>
          <Checkbox
            id='thirdYearConfirmed'
            checked={thirdYearConfirmed}
            onCheckedChange={(checked: boolean) => {
              setThirdYearConfirmed(checked === true);
              markTouched('thirdYearConfirmed');
            }}
            className='mt-0.5'
            aria-invalid={shouldShowError('thirdYearConfirmed', thirdYearConfirmed)}
            aria-describedby={shouldShowError('thirdYearConfirmed', thirdYearConfirmed) ? 'thirdYearConfirmed-error' : 'thirdYearConfirmed-description'}
          />
          <Label htmlFor='thirdYearConfirmed' className='cursor-pointer font-normal leading-relaxed'>
            {t('personal.thirdYear.label')}
            <span className='text-destructive ml-1'>*</span>
          </Label>
        </div>
        {shouldShowError('thirdYearConfirmed', thirdYearConfirmed) && (
          <p id='thirdYearConfirmed-error' className='text-xs text-destructive pl-7' role='alert'>
            {t('validation.thirdYearRequired')}
          </p>
        )}
        <p id='thirdYearConfirmed-description' className='text-xs text-muted-foreground pl-7'>
          {t('personal.thirdYear.description')}
        </p>
      </div>

      <Alert>
        <Icons.Info className='h-4 w-4' />
        <AlertDescription className='text-xs'>{t('qualifications.infoText')}</AlertDescription>
      </Alert>
    </div>
  );

  // DA-435: New step for substitute address (step 4 of 6)
  const renderAddress = () => (
    <div className='space-y-6'>
      <div className='space-y-4 rounded-lg border border-dashed border-border/80 p-4'>
        <p className='text-sm font-medium'>{t('personal.address')}</p>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='street'>
              {t('personal.street')}
              <span className='text-destructive ml-1'>*</span>
            </Label>
            <Input
              id='street'
              value={addressStreet}
              onChange={(event) => setAddressStreet(event.target.value)}
              onBlur={() => markTouched('addressStreet')}
              autoComplete='street-address'
              aria-invalid={shouldShowError('addressStreet', addressStreet.trim().length > 0)}
              aria-describedby={shouldShowError('addressStreet', addressStreet.trim().length > 0) ? 'addressStreet-error' : undefined}
            />
            {shouldShowError('addressStreet', addressStreet.trim().length > 0) && (
              <p id='addressStreet-error' className='text-xs text-destructive' role='alert'>
                {t('validation.streetRequired')}
              </p>
            )}
          </div>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='postalCode'>
                {t('personal.postalCode')}
                <span className='text-destructive ml-1'>*</span>
              </Label>
              <Input
                id='postalCode'
                value={addressPostalCode}
                onChange={(event) => setAddressPostalCode(event.target.value)}
                onBlur={() => markTouched('addressPostalCode')}
                autoComplete='postal-code'
                aria-invalid={shouldShowError('addressPostalCode', addressPostalCode.trim().length > 0)}
                aria-describedby={shouldShowError('addressPostalCode', addressPostalCode.trim().length > 0) ? 'addressPostalCode-error' : undefined}
              />
              {shouldShowError('addressPostalCode', addressPostalCode.trim().length > 0) && (
                <p id='addressPostalCode-error' className='text-xs text-destructive' role='alert'>
                  {t('validation.postalCodeRequired')}
                </p>
              )}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='city'>
                {t('personal.city')}
                <span className='text-destructive ml-1'>*</span>
              </Label>
              <Input
                id='city'
                value={addressCity}
                onChange={(event) => setAddressCity(event.target.value)}
                onBlur={() => markTouched('addressCity')}
                autoComplete='address-level2'
                aria-invalid={shouldShowError('addressCity', addressCity.trim().length > 0)}
                aria-describedby={shouldShowError('addressCity', addressCity.trim().length > 0) ? 'addressCity-error' : undefined}
              />
              {shouldShowError('addressCity', addressCity.trim().length > 0) && (
                <p id='addressCity-error' className='text-xs text-destructive' role='alert'>
                  {t('validation.cityRequired')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Alert>
        <Icons.Info className='h-4 w-4' />
        <AlertDescription className='text-xs'>{t('address.infoText')}</AlertDescription>
      </Alert>
    </div>
  );

  const toggleSelection = (list: string[], value: string, setter: (next: string[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  };

  const renderRecommendations = () => (
    <div className='space-y-6'>
      <div className='space-y-4 rounded-lg border border-border/80 p-4'>
        <p className='text-sm font-semibold'>{t('recommendations.kvRegions')}</p>
        <div className='rounded-lg border bg-muted/40 p-3'>
          <p className='mb-2 text-xs text-muted-foreground'>{t('recommendations.kvRegionsHint')}</p>
          <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
            {KV_REGIONS.map((kv) => {
              const checked = selectedKvs.includes(kv);
              return (
                <button
                  key={kv}
                  type='button'
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${checked ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:border-primary/60'}`}
                  onClick={() => toggleSelection(selectedKvs, kv, setSelectedKvs)}
                >
                  <span>{kv}</span>
                  {checked ? <Icons.Check className='h-4 w-4' /> : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className='space-y-4 rounded-lg border border-border/80 p-4'>
        <p className='text-sm font-semibold'>{t('recommendations.serviceTypes')}</p>
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
          {SERVICE_TYPES.map((service) => {
            const checked = serviceTypes.includes(service.value);
            return (
              <button
                key={service.value}
                type='button'
                className={`flex w-full items-start gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${checked ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:border-primary/60'}`}
                onClick={() => {
                  setServiceTypes((prev) => (prev.includes(service.value) ? prev.filter((item) => item !== service.value) : [...prev, service.value]));
                }}
              >
                <Icons.CheckCircle className={`mt-1 h-4 w-4 ${checked ? 'text-primary' : 'opacity-0 transition-opacity duration-150 group-hover:opacity-40'}`} />
                <div>
                  <p className='font-medium'>{service.label}</p>
                  <p className='text-xs text-muted-foreground'>{service.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className='flex items-start gap-3 rounded-md border border-border/80 p-3'>
          <Checkbox id='enableHighCompOverride' checked={highCompOverride} onCheckedChange={(checked: boolean) => setHighCompOverride(Boolean(checked))} />
          <div className='space-y-1'>
            <Label htmlFor='enableHighCompOverride' className='text-sm'>
              {t('recommendations.highCompOverride')}
            </Label>
            <p className='text-xs text-muted-foreground'>{t('recommendations.highCompOverrideDescription')}</p>
            {highCompOverride ? (
              <div className='flex items-center gap-2 pt-2'>
                <Input id='minCompensation' type='number' min={50} max={500} step={5} value={minComp} onChange={(event) => setMinComp(Number(event.target.value))} className='h-8 w-24 text-sm' />
                <span className='text-xs text-muted-foreground'>€/h</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className='space-y-4 rounded-lg border border-border/80 p-4'>
      <div className='flex items-start justify-between gap-1'>
        <div className='space-y-1'>
          <p className='text-sm font-semibold'>{t('documents.title')}</p>
          <p className='text-xs text-muted-foreground'>{t('documents.description')}</p>
        </div>
        <Button variant='ghost' size='sm' onClick={() => setShowDocs((prev) => !prev)}>
          {showDocs ? (
            <>
              <Icons.ChevronUp className='mr-1 h-4 w-4' />
              {t('documents.collapse')}
            </>
          ) : (
            <>
              <Icons.ChevronDown className='mr-1 h-4 w-4' />
              {t('documents.uploadNow')}
            </>
          )}
        </Button>
      </div>
      <Alert>
        <Icons.Info className='h-4 w-4' />
        <AlertDescription className='text-xs'>{t('documents.noDocumentsHint')}</AlertDescription>
      </Alert>
      {showDocs ? (
        <div className='space-y-6 rounded-lg border border-dashed border-border/60 p-4'>
          <DocumentUpload title={t('documents.approbation.title')} description={t('documents.approbation.description')} documentType='approbation' onUploadComplete={() => toast.success(t('toast.approbationUploaded'))} />
          <Separator />
          <DocumentUpload
            title={t('documents.insurance.title')}
            description={t('documents.insurance.description')}
            documentType='insurance'
            requireExpiration
            company={insuranceCompany}
            policyNumber={policyNumber}
            validFrom={validFrom}
            validTo={validTo}
            onCompanyChange={setInsuranceCompany}
            onPolicyNumberChange={setPolicyNumber}
            onValidFromChange={setValidFrom}
            onValidToChange={setValidTo}
            onUploadComplete={() => toast.success(t('toast.insuranceUploaded'))}
          />
        </div>
      ) : null}
      <div className='rounded-md border border-border/80 bg-muted/40 p-4 text-xs text-muted-foreground'>
        <p className='font-medium'>{t('documents.status')}</p>
        <p>{verificationStatus?.status ? t('documents.currentStatus', { status: verificationStatus.status }) : t('documents.noDocuments')}</p>
      </div>
    </div>
  );

  // DA-387: Provider location form (step 3 of 3)
  const renderLocation = () => {
    // Only providers use this step
    if (!isProvider) return null;

    return (
      <div className='space-y-6'>
        {/* Location/Business Info Section */}
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='invoiceName'>
              {t('location.invoiceName')}
              <span className='text-destructive ml-1'>*</span>
            </Label>
            <Input
              id='invoiceName'
              value={invoiceName}
              onChange={(event) => setInvoiceName(event.target.value)}
              onBlur={() => markTouched('invoiceName')}
              placeholder={t('location.invoiceNamePlaceholder')}
              aria-invalid={shouldShowError('invoiceName', invoiceName.trim().length > 0)}
              aria-describedby={shouldShowError('invoiceName', invoiceName.trim().length > 0) ? 'invoiceName-error' : 'invoiceName-description'}
            />
            {shouldShowError('invoiceName', invoiceName.trim().length > 0) && (
              <p id='invoiceName-error' className='text-xs text-destructive' role='alert'>
                {t('validation.invoiceNameRequired')}
              </p>
            )}
            {!shouldShowError('invoiceName', invoiceName.trim().length > 0) && (
              <p id='invoiceName-description' className='text-xs text-muted-foreground'>
                {t('location.invoiceNameDescription')}
              </p>
            )}
          </div>

          <Separator />
          <p className='text-sm font-medium'>{t('location.address')}</p>

          <div className='space-y-2'>
            <Label htmlFor='street'>
              {t('location.street')}
              <span className='text-destructive ml-1'>*</span>
            </Label>
            <Input
              id='street'
              value={addressStreet}
              onChange={(event) => setAddressStreet(event.target.value)}
              onBlur={() => markTouched('addressStreet')}
              autoComplete='street-address'
              aria-invalid={shouldShowError('addressStreet', addressStreet.trim().length > 0)}
              aria-describedby={shouldShowError('addressStreet', addressStreet.trim().length > 0) ? 'addressStreet-error' : undefined}
            />
            {shouldShowError('addressStreet', addressStreet.trim().length > 0) && (
              <p id='addressStreet-error' className='text-xs text-destructive' role='alert'>
                {t('validation.streetRequired')}
              </p>
            )}
          </div>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='postalCode'>
                {t('location.postalCode')}
                <span className='text-destructive ml-1'>*</span>
              </Label>
              <Input
                id='postalCode'
                value={addressPostalCode}
                onChange={(event) => setAddressPostalCode(event.target.value)}
                onBlur={() => markTouched('addressPostalCode')}
                autoComplete='postal-code'
                aria-invalid={shouldShowError('addressPostalCode', addressPostalCode.trim().length > 0)}
                aria-describedby={shouldShowError('addressPostalCode', addressPostalCode.trim().length > 0) ? 'addressPostalCode-error' : undefined}
              />
              {shouldShowError('addressPostalCode', addressPostalCode.trim().length > 0) && (
                <p id='addressPostalCode-error' className='text-xs text-destructive' role='alert'>
                  {t('validation.postalCodeRequired')}
                </p>
              )}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='city'>
                {t('location.city')}
                <span className='text-destructive ml-1'>*</span>
              </Label>
              <Input
                id='city'
                value={addressCity}
                onChange={(event) => setAddressCity(event.target.value)}
                onBlur={() => markTouched('addressCity')}
                autoComplete='address-level2'
                aria-invalid={shouldShowError('addressCity', addressCity.trim().length > 0)}
                aria-describedby={shouldShowError('addressCity', addressCity.trim().length > 0) ? 'addressCity-error' : undefined}
              />
              {shouldShowError('addressCity', addressCity.trim().length > 0) && (
                <p id='addressCity-error' className='text-xs text-destructive' role='alert'>
                  {t('validation.cityRequired')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* MVZ-specific fields (conditional) */}
        {isMvz && (
          <div className='space-y-4 rounded-lg border border-dashed border-border/80 p-4 bg-muted/20'>
            <p className='text-sm font-medium'>{t('location.mvzSection')}</p>
            <div className='space-y-2'>
              <Label htmlFor='bsnr'>{t('location.bsnr')}</Label>
              <Input id='bsnr' value={bsnr} onChange={(event) => setBsnr(event.target.value)} placeholder={t('location.bsnrPlaceholder')} maxLength={9} aria-describedby='bsnr-description' />
              <p id='bsnr-description' className='text-xs text-muted-foreground'>
                {t('location.bsnrDescription')}
              </p>
            </div>

            {/* Doctors array */}
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <Label>
                  {t('location.doctors')}
                  <span className='text-destructive ml-1'>*</span>
                </Label>
                <Button type='button' variant='outline' size='sm' onClick={handleAddDoctor}>
                  <Icons.Plus className='mr-1 h-4 w-4' />
                  {t('location.addDoctor')}
                </Button>
              </div>

              {doctors.map((doctor, idx) => (
                <div key={idx} className='space-y-3 rounded-md border border-border/60 p-3'>
                  <div className='flex items-center justify-between'>
                    <p className='text-xs font-medium text-muted-foreground'>{t('location.doctorNumber', { number: idx + 1 })}</p>
                    {doctors.length > 1 && (
                      <Button type='button' variant='ghost' size='sm' onClick={() => handleRemoveDoctor(idx)} className='h-6 px-2 text-destructive hover:text-destructive'>
                        <Icons.Trash className='h-3 w-3' />
                      </Button>
                    )}
                  </div>
                  <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                    <div className='space-y-1'>
                      <Label htmlFor={`doctor-${idx}-fullName`} className='text-xs'>
                        {t('location.doctorName')}
                        <span className='text-destructive ml-1'>*</span>
                      </Label>
                      <Input
                        id={`doctor-${idx}-fullName`}
                        value={doctor.fullName}
                        onChange={(event) => updateDoctor(idx, 'fullName', event.target.value)}
                        onBlur={() => markTouched(`doctor-${idx}-fullName`)}
                        placeholder={t('location.doctorNamePlaceholder')}
                        className='h-8 text-sm'
                        aria-invalid={shouldShowError(`doctor-${idx}-fullName`, doctor.fullName.trim().length > 0)}
                      />
                      {shouldShowError(`doctor-${idx}-fullName`, doctor.fullName.trim().length > 0) && <p className='text-xs text-destructive'>{t('validation.doctorNameRequired')}</p>}
                    </div>
                    <div className='space-y-1'>
                      <Label htmlFor={`doctor-${idx}-lanr`} className='text-xs'>
                        {t('location.lanr')}
                        <span className='text-destructive ml-1'>*</span>
                      </Label>
                      <Input
                        id={`doctor-${idx}-lanr`}
                        value={doctor.lanr}
                        onChange={(event) => updateDoctor(idx, 'lanr', event.target.value)}
                        onBlur={() => markTouched(`doctor-${idx}-lanr`)}
                        placeholder={t('location.lanrPlaceholder')}
                        maxLength={9}
                        className='h-8 text-sm'
                        aria-invalid={shouldShowError(`doctor-${idx}-lanr`, doctor.lanr.trim().length > 0)}
                      />
                      {shouldShowError(`doctor-${idx}-lanr`, doctor.lanr.trim().length > 0) && <p className='text-xs text-destructive'>{t('validation.lanrRequired')}</p>}
                    </div>
                    <div className='space-y-1'>
                      <Label htmlFor={`doctor-${idx}-kvSitz`} className='text-xs'>
                        {t('location.kvSitz')}
                      </Label>
                      <Select
                        value={doctor.kvSitz}
                        onValueChange={(value: string | null) => {
                          if (value !== null) updateDoctor(idx, 'kvSitz', value);
                        }}
                      >
                        <SelectTrigger id={`doctor-${idx}-kvSitz`} className='h-8 text-sm'>
                          <SelectValue placeholder={t('location.kvSitzPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='0.25'>0,25</SelectItem>
                          <SelectItem value='0.5'>0,5</SelectItem>
                          <SelectItem value='0.75'>0,75</SelectItem>
                          <SelectItem value='1'>1,0</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Alert>
          <Icons.Info className='h-4 w-4' />
          <AlertDescription className='text-xs'>{t('location.dataUsage')}</AlertDescription>
        </Alert>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl space-y-6' data-testid='onboarding-dialog'>
        <DialogHeader>
          <DialogTitle>{isProvider ? t('dialog.providerTitle') : t('dialog.substituteTitle')}</DialogTitle>
          <DialogDescription>{t('dialog.description')}</DialogDescription>
        </DialogHeader>
        {error ? (
          <Alert variant='destructive' role='alert'>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className='flex items-center justify-between rounded-md border border-border/80 bg-muted/30 px-4 py-2 text-sm' data-testid='onboarding-step-indicator'>
          <div className='flex items-center gap-2'>
            <span className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground'>{stepIndex + 1}</span>
            <span data-testid='onboarding-step-label'>{labels[step]}</span>
          </div>
          <p className='text-xs text-muted-foreground'>{t('step', { step: stepIndex + 1, total: STEPS.length })}</p>
        </div>

        {step === 'intro' && renderIntro()}
        {step === 'personal' && <div className='max-h-[440px] overflow-y-auto pr-4'>{renderPersonal()}</div>}
        {step === 'qualifications' && <div className='max-h-[440px] overflow-y-auto pr-4'>{renderQualifications()}</div>}
        {step === 'address' && <div className='max-h-[440px] overflow-y-auto pr-4'>{renderAddress()}</div>}
        {step === 'recommendations' && <div className='max-h-[440px] overflow-y-auto pr-4'>{renderRecommendations()}</div>}
        {step === 'documents' && <div className='max-h-[440px] overflow-y-auto pr-4'>{renderDocuments()}</div>}
        {step === 'location' && <div className='max-h-[440px] overflow-y-auto pr-4'>{renderLocation()}</div>}

        <DialogFooter className='flex justify-between gap-2 sm:flex-row'>
          <Button variant='ghost' onClick={prevStep} disabled={stepIndex === 0 || isSaving}>
            {t('dialog.back')}
          </Button>
          <div className='flex gap-2'>
            {step === 'intro' ? (
              <Button onClick={nextStep} data-testid='onboarding-next'>
                {t('dialog.next')}
              </Button>
            ) : null}
            {step === 'personal' ? (
              <Button onClick={savePersonal} disabled={!canAdvance || isSaving} data-testid='onboarding-save-continue'>
                {isSaving ? <Icons.Loader className='mr-2 h-4 w-4 animate-spin' /> : null}
                {t('dialog.saveAndContinue')}
              </Button>
            ) : null}
            {step === 'qualifications' ? (
              <Button onClick={saveQualifications} disabled={!canAdvance || isSaving} data-testid='onboarding-save-continue'>
                {isSaving ? <Icons.Loader className='mr-2 h-4 w-4 animate-spin' /> : null}
                {t('dialog.saveAndContinue')}
              </Button>
            ) : null}
            {step === 'address' ? (
              <Button onClick={saveAddress} disabled={!canAdvance || isSaving} data-testid='onboarding-save-continue'>
                {isSaving ? <Icons.Loader className='mr-2 h-4 w-4 animate-spin' /> : null}
                {t('dialog.saveAndContinue')}
              </Button>
            ) : null}
            {step === 'recommendations' ? (
              <Button onClick={savePreferences} disabled={!canAdvance || isSaving} data-testid='onboarding-save-continue'>
                {isSaving ? <Icons.Loader className='mr-2 h-4 w-4 animate-spin' /> : null}
                {t('dialog.saveAndContinue')}
              </Button>
            ) : null}
            {step === 'documents' ? (
              <Button onClick={handleFinish} data-testid='onboarding-finish'>
                {t('dialog.finish')}
              </Button>
            ) : null}
            {step === 'location' && isProvider ? (
              <Button onClick={saveProviderLocation} disabled={!canAdvance || isSaving} data-testid='onboarding-complete'>
                {isSaving ? <Icons.Loader className='mr-2 h-4 w-4 animate-spin' /> : null}
                {t('dialog.completeOnboarding')}
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingDialog;
